import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecipeStudio } from './RecipeStudioContext';
import ChefList from '@/components/studio/ChefList';
import ChefBottomSheet from '@/components/studio/ChefBottomSheet';
import { parseChefMarkdown, ChefInfo } from '@/lib/chef-utils';
import { Chef } from '@/types/chef';
import { chefPersonas } from '@/config/chefPersonas';
import { getChefVisualTheme } from '@/config/chefVisualThemes';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getAllMarkdownPrompts } from '@/lib/getAllMarkdownPrompts';
import BottomActionBar from './BottomActionBar';
import { Info, Sparkles, Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

const Step2PersonaComplexity: React.FC = () => {
  const { selectedPersona, setSelectedPersona, complexity, setComplexity } = useRecipeStudio();
  const navigate = useNavigate();
  const [allChefDetails, setAllChefDetails] = useState<Record<string, ChefInfo>>({});
  const [bottomSheetChef, setBottomSheetChef] = useState<Chef | null>(null);
  const [hoveredChef, setHoveredChef] = useState<string | null>(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    try {
      const { personas } = getAllMarkdownPrompts();
      const markdown = personas['Six_chefs'];
      setAllChefDetails(parseChefMarkdown(markdown));
    } catch {
    }
  }, []);

  const handleSelectPersona = (id: string) => {
    if (!isDesktop) {
      // On mobile, show bottom sheet on first tap, select on tap if already viewing
      const chef = chefPersonas.find(c => c.id === id);
      if (chef) {
        if (selectedPersona === id) {
          // Already selected, open details
          setBottomSheetChef(chef);
        } else {
          // First tap: select the chef
          setSelectedPersona(id);
        }
      }
    } else {
      setSelectedPersona(id);
    }
  };

  const handleNext = () => {
    if (!selectedPersona) return;
    navigate('../step/3');
  };

  const handleBack = () => navigate('../step/1');

  useMemo(() => {
    if (!selectedPersona || !allChefDetails[selectedPersona]) return null;
    const details = allChefDetails[selectedPersona];
    return {
      description: details.description,
      points: details.points
    }
  }, [selectedPersona, allChefDetails]);

  // Get the current selected chef object
  const selectedChefObject = useMemo(() => {
    return chefPersonas.find(c => c.id === selectedPersona) || null;
  }, [selectedPersona]);

  // Enrich chef with specialties from parsed markdown
  const enrichedBottomSheetChef = useMemo(() => {
    if (!bottomSheetChef) return null;
    const details = allChefDetails[bottomSheetChef.id];
    if (details) {
      return {
        ...bottomSheetChef,
        specialties: details.points.slice(0, 4),
        personality: details.description
      };
    }
    return bottomSheetChef;
  }, [bottomSheetChef, allChefDetails]);

  // Get enriched details for any chef
  const getChefSpecialties = (chefId: string): string[] => {
    const details = allChefDetails[chefId];
    return details?.points?.slice(0, 3) || [];
  };

  // Desktop Layout - Horizontal Avatar Carousel + Expanded Detail Panel
  // DESIGN NOTE: Compact avatars allow entire page to fit above fold
  if (isDesktop) {
    const selectedChef = chefPersonas.find(c => c.id === selectedPersona);
    const selectedSpecialties = selectedPersona ? getChefSpecialties(selectedPersona) : [];

    return (
      <div className="max-w-4xl mx-auto px-6 py-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
            Choose Your Culinary Guide
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Each chef brings a unique philosophy to your recipe
          </p>
        </motion.div>

        {/* Avatar Carousel - Horizontally scrollable row of circular avatars */}
        <div className="relative mb-6">
          <div className="flex justify-center gap-4 py-2">
            {chefPersonas.map((chef, index) => {
              const isSelected = selectedPersona === chef.id;
              const isHovered = hoveredChef === chef.id;

              return (
                <motion.button
                  key={chef.id}
                  onClick={() => handleSelectPersona(chef.id)}
                  onMouseEnter={(e) => {
                    setHoveredChef(chef.id);
                    const video = e.currentTarget.querySelector('video');
                    if (video) {
                      video.currentTime = 0;
                      video.play().catch(() => { });
                    }
                  }}
                  onMouseLeave={(e) => {
                    setHoveredChef(null);
                    const video = e.currentTarget.querySelector('video');
                    if (video) {
                      video.pause();
                      video.currentTime = 0;
                    }
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center gap-2 group focus:outline-none min-w-[44px] min-h-[44px]"
                >
                  {/* Avatar Container */}
                  <div className={cn(
                    "relative w-20 h-20 rounded-full overflow-hidden transition-all duration-300",
                    isSelected
                      ? "ring-[3px] ring-orange-500 ring-offset-2 ring-offset-white shadow-lg shadow-orange-500/30"
                      : "ring-2 ring-slate-200 group-hover:ring-orange-300"
                  )}>
                    {/* Video Layer */}
                    {chef.video && (
                      <video
                        src={chef.video}
                        className={cn(
                          "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
                          isHovered ? "opacity-100" : "opacity-0"
                        )}
                        muted
                        loop
                        playsInline
                        preload="none"
                      />
                    )}

                    {/* Image Layer */}
                    <img
                      src={chef.avatar}
                      alt={chef.name}
                      className={cn(
                        "w-full h-full object-cover transition-all duration-300",
                        isHovered && chef.video && "opacity-0"
                      )}
                    />

                    {/* Selected Checkmark */}
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          className="absolute inset-0 bg-orange-500/20 flex items-center justify-center"
                        >
                          <div className="w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                            <Check className="w-4 h-4 text-white" strokeWidth={3} />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Name Label */}
                  <span className={cn(
                    "text-xs font-medium text-center leading-tight max-w-[90px] transition-colors duration-200",
                    isSelected
                      ? "text-orange-600"
                      : "text-slate-600 group-hover:text-slate-900"
                  )}>
                    {chef.name.replace('The ', '')}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Expanded Chef Detail Panel - Appears when chef is selected */}
        <AnimatePresence mode="wait">
          {selectedChef ? (
            <motion.div
              key={selectedChef.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="bg-white rounded-2xl shadow-lg ring-1 ring-slate-100 p-5 mb-4"
            >
              <div className="flex items-center gap-5">
                {/* Circular Video/Image */}
                <div className="relative w-20 h-20 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-orange-200 shadow-lg">
                  {selectedChef.video ? (
                    <video
                      src={selectedChef.video}
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      src={selectedChef.avatar}
                      alt={selectedChef.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Info Section */}
                <div className="flex-1 min-w-0">
                  <h2 className={cn(
                    "text-lg font-bold mb-1",
                    getChefVisualTheme(selectedChef.id).accentColor
                  )}>
                    {selectedChef.name}
                  </h2>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                    {selectedChef.description}
                  </p>

                  {/* Bulleted Specialties */}
                  {selectedSpecialties.length > 0 && (
                    <ul className="space-y-1">
                      {selectedSpecialties.slice(0, 3).map((specialty, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Sparkles className={cn(
                            "w-3.5 h-3.5 mt-0.5 shrink-0",
                            getChefVisualTheme(selectedChef.id).accentColorLight
                          )} />
                          <span className="text-slate-700 leading-snug">
                            {specialty}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-slate-50 rounded-2xl p-6 text-center mb-4"
            >
              <p className="text-slate-500 text-sm">
                Select a chef above to see their culinary philosophy
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Complexity Section - Compact inline */}
        <AnimatePresence mode="wait">
          {selectedPersona && selectedPersona !== 'TheEssentialistChef' && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="bg-white rounded-xl px-4 py-3 shadow-sm ring-1 ring-slate-100 mb-4"
            >
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-slate-700 shrink-0">
                  Complexity
                </span>
                <div className="flex-1">
                  <Slider
                    min={1}
                    max={3}
                    step={1}
                    value={[complexity]}
                    onValueChange={(value) => setComplexity(value[0])}
                  />
                </div>
                <div className={cn(
                  "px-3 py-1 rounded-full text-xs font-semibold min-w-[75px] text-center",
                  complexity === 1 && "bg-green-100 text-green-700",
                  complexity === 2 && "bg-amber-100 text-amber-700",
                  complexity === 3 && "bg-red-100 text-red-700"
                )}>
                  {complexity === 1 ? 'Simple' : complexity === 2 ? 'Standard' : 'Ambitious'}
                </div>
              </div>
            </motion.div>
          )}

          {selectedPersona === 'TheEssentialistChef' && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="bg-slate-50 rounded-xl px-4 py-3 text-center mb-4"
            >
              <p className="text-xs text-slate-600">
                ✨ The Essentialist Chef focuses on purity — complexity doesn't apply
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Bar - Clean, just Back and Continue */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handleBack}
            className="rounded-full px-6 h-11"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={!selectedPersona}
            className={cn(
              "rounded-full px-8 h-11 transition-all duration-300",
              selectedPersona
                ? "bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30"
                : "bg-slate-200 text-slate-500"
            )}
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Mobile Layout - Keep existing carousel approach
  return (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-3 flex-shrink-0">
        <h1 className="text-xl sm:text-3xl font-bold text-slate-900">Choose Your Culinary Guide</h1>
        <p className="text-sm sm:text-base text-slate-600 mt-1">Select a chef to define the style of your recipe</p>
      </div>

      {/* Mobile: Carousel with inline selection summary */}
      <div className="px-1 space-y-4">
        {/* Chef Carousel */}
        <ChefList
          chefs={chefPersonas}
          selectedPersona={selectedPersona}
          onSelectPersona={handleSelectPersona}
        />

        {/* Selected Chef Summary Card */}
        {selectedChefObject && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-full border-2 border-orange-300 bg-white shadow-sm overflow-hidden shrink-0">
                  <img
                    src={selectedChefObject.avatar}
                    alt={selectedChefObject.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className={cn(
                    "font-semibold truncate",
                    getChefVisualTheme(selectedChefObject.id).accentColor
                  )}>
                    {selectedChefObject.name}
                  </h3>
                  <p className="text-xs text-slate-600">Your selected guide</p>
                </div>
              </div>
              <button
                onClick={() => setBottomSheetChef(selectedChefObject)}
                className="p-2 h-10 w-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:text-orange-500 transition-colors shrink-0"
                aria-label="Chef details"
              >
                <Info className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 line-clamp-2">
              {selectedChefObject.description}
            </p>
          </motion.div>
        )}

        {/* Hint when no chef selected */}
        {!selectedPersona && (
          <div className="text-center py-4">
            <p className="text-slate-500 text-sm">
              Tap a chef above to select them
            </p>
          </div>
        )}

        {/* Bottom Sheet for Chef Details */}
        <ChefBottomSheet
          chef={enrichedBottomSheetChef}
          isOpen={!!bottomSheetChef}
          onClose={() => setBottomSheetChef(null)}
          onSelect={(id) => setSelectedPersona(id)}
          isSelected={bottomSheetChef?.id === selectedPersona}
        />

        {/* Mobile action bar */}
        <BottomActionBar
          onBack={handleBack}
          onNext={selectedPersona ? handleNext : undefined}
          backLabel="Back"
          nextLabel="Next"
          nextDisabled={!selectedPersona}
          hideOnDesktop
        />
      </div>
    </div>
  );
};

export default Step2PersonaComplexity;